import { safeParseJSON } from '@/utils/json-parse';

import type { DisplayConfigItem } from './types';

function pickDisplayField(item: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = item[key];
    if (value != null && value !== '') return String(value);
  }
  return '';
}

function normalizeDisplayConfigItem(raw: unknown): DisplayConfigItem | null {
  if (!raw || typeof raw !== 'object') return null;

  const item = raw as Record<string, unknown>;
  const key = pickDisplayField(item, ['key', 'Key', 'name', 'displayName', 'label']);
  const value = pickDisplayField(item, ['value', 'Value', 'displayValue', 'content']);
  if (!key && !value) return null;

  const id = pickDisplayField(item, ['id', 'ID']) || key || value;
  const tag = item.tag != null && item.tag !== '' ? String(item.tag) : undefined;

  return {
    id,
    key: key || '-',
    value: value || '-',
    tag,
  };
}

function normalizeDisplayConfigList(items: unknown[]): DisplayConfigItem[] {
  return items.map(normalizeDisplayConfigItem).filter((item): item is DisplayConfigItem => !!item);
}

function unwrapDisplayConfigValue(value: unknown): unknown {
  let current = value;

  for (let i = 0; i < 3; i++) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return [];
    const parsed = safeParseJSON(trimmed, null);
    if (parsed === null || parsed === current) break;
    current = parsed;
  }

  return current;
}

/** 解析 displayConfig：兼容 JSON 字符串（含多层编码）、数组、对象（旧数据） */
export function parseDisplayConfig(value: unknown): DisplayConfigItem[] {
  if (value == null || value === '') return [];

  const current = unwrapDisplayConfigValue(value);

  if (Array.isArray(current)) {
    return normalizeDisplayConfigList(current);
  }

  if (current && typeof current === 'object') {
    const obj = current as Record<string, unknown>;
    const nested = obj.items ?? obj.list ?? obj.displayConfig ?? obj.display_config;
    if (Array.isArray(nested)) {
      return normalizeDisplayConfigList(nested);
    }

    const values = Object.values(obj);
    if (values.length && values.every((item) => item && typeof item === 'object')) {
      return normalizeDisplayConfigList(values);
    }
  }

  return [];
}
